import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AsyncBoundary, Drawer, Icon, useToast } from '@kollez/design-system';
import { useGroupAnalytics, type GroupAnalyticsRow } from '@/lib/queries';

/**
 * /group/colleges (F95a) — direct port of kollez/pages/group/colleges.html.
 *   KPIs (total / on track / behind / no TPO), segmented (All/On track/Behind/Needs setup)
 *   filters (type + university), search, table with .pct-cell + placed-vs-target bars,
 *   drawer with stats + dl + target edit + Save-target / Open-college / Assign-officer.
 */

type Health = 'all' | 'ok' | 'behind' | 'setup';

interface CollegeUI extends GroupAnalyticsRow {
  collegeId: string;
  target: number;
  tpo?: string;
  city?: string;
  university?: string;
  health: Health;
  placedPct: number;
}

function toUi(r: GroupAnalyticsRow): CollegeUI {
  // F-089: previous version pinned target = 45 (a group-wide client default)
  // and named JNTU Kakinada as the university for every row. College has no
  // target field yet — use plain absolute thresholds instead (ok >= 45,
  // behind 30-44, setup below). University stays '—' until server sends it.
  const placedPct = r.drives > 0 ? Math.round((r.offersAccepted / r.drives) * 100) : 0;
  const health: Health = !r.type ? 'setup' : placedPct >= 45 ? 'ok' : placedPct >= 30 ? 'behind' : 'setup';
  return { ...r, collegeId: r._id, target: 0, placedPct, health, city: '—', university: '—' };
}

export function GroupCollegesPage() {
  const { data, isLoading, error, refetch } = useGroupAnalytics();
  const toast = useToast();
  const [seg, setSeg] = useState<Health>('all');
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [targetDraft, setTargetDraft] = useState<string>('');

  const rows: CollegeUI[] = useMemo(() => (data?.colleges ?? []).map(toUi), [data]);
  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      const okSeg = seg === 'all' || r.health === seg;
      const okType = !type || r.type === type;
      const okQ = !ql || `${r.name} ${r.type} ${r.city ?? ''}`.toLowerCase().includes(ql);
      return okSeg && okType && okQ;
    });
  }, [rows, seg, type, q]);

  const kpis = useMemo(() => ({
    total: rows.length,
    ok: rows.filter((r) => r.health === 'ok').length,
    behind: rows.filter((r) => r.health === 'behind').length,
    setup: rows.filter((r) => r.health === 'setup').length,
  }), [rows]);

  const opened = openId ? rows.find((r) => r.collegeId === openId) : null;

  return (
    <div className="stack">
      <style>{`
        .pct-cell { display: grid; grid-template-columns: 44px minmax(80px, 160px); gap: 10px; align-items: center; }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Colleges</h1>
          <p className="subhead">
            {kpis.total} colleges under the group. Targets are set here; each college's placement officer runs its drives.
          </p>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-outline" to="/group/structure"><Icon name="network" size={14} />Institution structure</Link>
          <Link className="btn btn-primary" to="/group/structure"><Icon name="plus" size={14} />Add college</Link>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <div className="card kpi">
          <span className="kpi-label">Colleges</span>
          <span className="kpi-value">{kpis.total}</span>
          <span className="kpi-meta">under the group</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">On track</span>
          <span className="kpi-value">{kpis.ok}</span>
          <span className="kpi-meta">at or above target</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Behind target</span>
          <span className="kpi-value">{kpis.behind}</span>
          <span className="kpi-meta">up to 15 points below</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Needs setup</span>
          <span className="kpi-value">{kpis.setup}</span>
          <span className="kpi-meta">no TPO or far behind</span>
        </div>
      </div>

      <div className="card">
        <div className="filterbar">
          <div className="input-group fb-search">
            <Icon name="search" size={14} />
            <input className="input" placeholder="College or city" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="segmented">
            {([['all', 'All'], ['ok', 'On track'], ['behind', 'Behind'], ['setup', 'Needs setup']] as const).map(([v, l]) => (
              <button key={v} aria-pressed={seg === v} onClick={() => setSeg(v)}>{l}</button>
            ))}
          </div>
          <select className="select" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">Any type</option>
            <option>Engineering</option>
            <option>Pharmacy</option>
            <option>Management</option>
            <option>Degree</option>
            <option>Polytechnic</option>
          </select>
          <span className="fb-count">{shown.length} of {rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={isLoading}
          error={error}
          empty={!isLoading && shown.length === 0}
          emptyTitle="No colleges match"
          emptyHint="Try a different filter or add a college through the structure tree."
          onRetry={() => void refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>College</th>
                  <th className="hide-mobile">Type</th>
                  <th className="r hide-mobile">Final year</th>
                  <th>Placed vs target</th>
                  <th className="hide-mobile">Placement officer</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const gap = r.placedPct - r.target;
                  const tone = gap <= -15 ? 'danger' : gap < 0 ? 'warning' : 'success';
                  return (
                    <tr
                      key={r.collegeId}
                      onClick={() => { setOpenId(r.collegeId); setTargetDraft(String(r.target)); }}
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="flex items-center gap-3">
                          <span className="logo-tile">{r.name.slice(0, 3).toUpperCase()}</span>
                          <div className="min-w-0">
                            <div className="cell-title">{r.name}</div>
                            <div className="cell-sub">
                              {r.city ?? '—'} · {r.university ?? '—'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="hide-mobile">{r.type || '—'}</td>
                      <td className="r hide-mobile">—</td>
                      <td>
                        <div className="pct-cell">
                          <b className="num" style={{ color: tone === 'danger' ? 'rgb(var(--ink-danger))' : undefined }}>
                            {r.placedPct}%
                          </b>
                          <div className={`bar ${tone}`}>
                            <span style={{ width: `${Math.min(100, r.placedPct)}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="hide-mobile">
                        {r.tpo ? <span className="text-sm">{r.tpo}</span> : <span className="badge badge-danger">Vacant</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Drawer open={Boolean(opened)} onClose={() => setOpenId(null)} title={opened?.name ?? ''} size="lg">
        {opened && (
          <>
            <p className="eyebrow">{opened.type || 'College'} · {opened.city ?? '—'}</p>
            <h2 className="h-section">{opened.name}</h2>

            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="well p-3"><div className="helper">Placed</div><b className="text-lg">{opened.placedPct}%</b></div>
              <div className="well p-3"><div className="helper">Target</div><b className="text-lg">{opened.target}%</b></div>
              <div className="well p-3"><div className="helper">Drives</div><b className="text-lg">{opened.drives}</b></div>
            </div>

            <dl className="dl mt-4">
              <dt>University</dt><dd>{opened.university ?? '—'}</dd>
              <dt>Placement officer</dt>
              <dd>{opened.tpo ? opened.tpo : <span style={{ color: 'rgb(var(--ink-danger))' }}>Vacant</span>}</dd>
              <dt>Accreditation</dt><dd>NAAC A · AICTE approved</dd>
              <dt>Median CTC</dt><dd>{opened.avgCtc ? `₹${opened.avgCtc.toLocaleString('en-IN')}` : '—'}</dd>
            </dl>

            <label className="grid gap-1.5 mt-4">
              <span className="label">Placement target, this season</span>
              <div className="flex items-center gap-2">
                <input className="input" type="number" value={targetDraft} onChange={(e) => setTargetDraft(e.target.value)} style={{ width: 100 }} />
                <span className="helper">% of final-year students seeking jobs</span>
              </div>
            </label>

            <div className="drawer-foot">
              {!opened.tpo && <Link className="btn btn-outline" to="/group/team">Assign placement officer</Link>}
              <button
                className="btn btn-ghost"
                disabled
                title="College-target field does not exist on the College schema yet — ships alongside targets:manage endpoint."
              >
                Save target · soon
              </button>
              <Link className="btn btn-primary ml-auto" to={`/group/colleges/${opened.collegeId}`}>Open college</Link>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}
